/**
 * Thin HTTP client for system-api `/log/v1` (spec 0015 fase B / request 0348; activity 0351).
 */

import { systemApiFetch } from '@/services/systemApiClient'

const MOUNT = '/log/v1'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'
export type LogKind = 'log' | 'activity'
export type LogOutcome = 'ok' | 'error' | 'cancel'

export type LogRecordItem = {
  id: string
  ts: string
  kind: LogKind | string
  level: LogLevel
  message: string
  details: Record<string, unknown> | null
  userId: string | null
  sessionId: string | null
  app: string | null
  appVersion: string | null
  url: string | null
  context: Record<string, unknown> | null
  action: string | null
  module: string | null
  entity: string | null
  entityId: string | null
  outcome: string | null
  durationMs: number | null
  attrs: Record<string, unknown> | null
  traceId: string | null
}

export type LogRecordsPage = {
  items: LogRecordItem[]
  nextCursor?: string
}

export type LogRecordsQuery = {
  userId?: string
  from?: string
  to?: string
  levels?: LogLevel[]
  apps?: string[]
  sessionId?: string
  kinds?: LogKind[]
  action?: string[]
  entity?: string
  entityId?: string
  traceId?: string
  /** Platform admin only — `org_id IS NULL` events. */
  scope?: 'system'
  q?: string
  limit?: number
  cursor?: string
}

export function fetchLogRecords(
  orgId: string | undefined,
  params: LogRecordsQuery = {},
): Promise<LogRecordsPage> {
  const q = new URLSearchParams()
  if (params.userId) q.set('userId', params.userId)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  if (params.levels && params.levels.length > 0) q.set('levels', params.levels.join(','))
  if (params.apps && params.apps.length > 0) q.set('apps', params.apps.join(','))
  if (params.sessionId) q.set('sessionId', params.sessionId)
  if (params.kinds && params.kinds.length > 0) q.set('kinds', params.kinds.join(','))
  if (params.action && params.action.length > 0) q.set('action', params.action.join(','))
  if (params.entity) q.set('entity', params.entity)
  if (params.entityId) q.set('entityId', params.entityId)
  if (params.traceId) q.set('traceId', params.traceId)
  if (params.scope) q.set('scope', params.scope)
  if (params.q) q.set('q', params.q)
  if (params.limit != null) q.set('limit', String(params.limit))
  if (params.cursor) q.set('cursor', params.cursor)
  const qs = q.toString()
  return systemApiFetch<LogRecordsPage>(MOUNT, qs ? `/records?${qs}` : '/records', {
    method: 'GET',
    // scope=system does not require X-Organization-Id
    orgId: params.scope === 'system' ? undefined : orgId,
  })
}

/** Effective persist minLevel for the caller (request 0350). */
export type LogClientConfig = {
  minLevel: 'debug' | 'info'
  until?: string
}

export type LogDebugGrant = {
  userId: string
  until: string
  grantedBy: string
  grantedAt: string
}

export type LogDebugGrantHours = 1 | 4 | 24

export function fetchLogConfig(orgId: string): Promise<LogClientConfig> {
  return systemApiFetch<LogClientConfig>(MOUNT, '/config', {
    method: 'GET',
    orgId,
  })
}

export function fetchLogDebugGrants(orgId: string): Promise<LogDebugGrant[]> {
  return systemApiFetch<LogDebugGrant[]>(MOUNT, '/debug-grants', {
    method: 'GET',
    orgId,
  })
}

export function putLogDebugGrant(
  orgId: string,
  userId: string,
  hours: LogDebugGrantHours,
): Promise<LogDebugGrant> {
  return systemApiFetch<LogDebugGrant>(MOUNT, `/debug-grants/${encodeURIComponent(userId)}`, {
    method: 'PUT',
    orgId,
    body: JSON.stringify({ hours }),
  })
}

export function deleteLogDebugGrant(orgId: string, userId: string): Promise<void> {
  return systemApiFetch<void>(MOUNT, `/debug-grants/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    orgId,
  })
}
